import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { Company } from '@core/models/common/company';
import { NavBarItem } from '@shell/ui/nav-bar/nav-bar-item';
import { SessionService } from '@core/state/session/session.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { NAV_BAR_ITEMS } from './nav-bar-items';
import { StateMatchType } from '@shared/directives/state-ref/state-match-type';
import { RouterService } from '@core/state/router/router.service';
import { User } from '@core/models/user/user';
import { AuthService } from '@core/state/auth/auth.service';
import {
    BILLING,
    DELIVERIES_SETTINGS,
    DEVSUPPORT_DASHBOARD,
    LIVEVIEW,
    LOCATIONS,
    PROFILE
} from '@core/router/app-states';
import { BillingService } from '@features/billing/billing.service';
import { BillingAlert } from '@core/models/billing/billing-alert';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import { FEATURE } from '@core/auth/feature-id';
import { AccountRole } from '@core/models/account/account-role';
import { IAppRouterState } from '@core/state/router/router-state';
import { WindowRef } from '@vendor/window/window.component';
import { environment } from '@environments/environment';

@Component({
    selector: 'ihd-nav-bar',
    templateUrl: './nav-bar.component.html',
    styleUrls: [
      './nav-bar.component.scss'
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      class: 'c-nav-bar',
      '[class.is-hidden]': '!showNavBar',
      '[class.c-nav-bar--live-view]': 'isLiveView'
    }
})
export class NavBarComponent implements OnInit, OnDestroy {

    public readonly FEATURE = FEATURE;
    public readonly DELIVERIES_SETTINGS = DELIVERIES_SETTINGS;

    public user: User;
    public company: Company;
    public items: NavBarItem[] = NAV_BAR_ITEMS;
    public STATE_STARTS_WITH: StateMatchType = StateMatchType.STARTS_WITH;
    public showDropdown: boolean = false;
    public billingAlert: BillingAlert;
    public TNASubscription: BillingSubscription;
    public showNavBar: boolean = true;
    public isLiveView: boolean;
    public isDevSupport: boolean;
    public isAdmin: boolean;
    public isManager: boolean;

    private destroyed$: Subject<void> = new Subject();
    private nativeWindow: Window;
    private readonly feedbackUrl: string = environment.assets.feedbackUrl;

    constructor(private cdr: ChangeDetectorRef,
                private windowRef: WindowRef,
                private authService: AuthService,
                private routerService: RouterService,
                private billingService: BillingService,
                private sessionService: SessionService) {
        this.nativeWindow = this.windowRef.nativeWindow;
    }

    ngOnInit() {

        this.routerService.getCurrentState()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((appRouterState: IAppRouterState) => {
                this.showNavBar = appRouterState?.name?.indexOf(DEVSUPPORT_DASHBOARD) !== 0;
                this.isLiveView = appRouterState?.name?.indexOf(LIVEVIEW) === 0;
                this.cdr.markForCheck();
            });

        this.sessionService.getCompany()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((company: Company) => {
                this.isAdmin = company?.role === AccountRole.ADMIN;
                this.isManager = company?.role === AccountRole.MANAGER;
                this.cdr.markForCheck();
            });

        this.authService.isDevSupport()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((isDevSupport: boolean) => {
                this.isDevSupport = isDevSupport;
                this.cdr.markForCheck();
            });

        this.billingService.getSubscription()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((subscription) => {
                this.TNASubscription = subscription;
            });

        this.billingService
            .getNavBarAlert()
            .pipe(
                filter(
                    () =>
                        this.TNASubscription &&
                        !this.TNASubscription.isCancelled &&
                        !this.TNASubscription.isTrial
                ),
                takeUntil(this.destroyed$)
            )
            .subscribe((alert: BillingAlert) => {
                this.billingAlert = alert;
                this.cdr.markForCheck();
            });

        this.sessionService.getUser()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((user: User) => {
                this.user = user;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    goTo(state) {
        this.routerService.go(state);
    }


    toggleDropdown() {
        this.showDropdown = !this.showDropdown;
        this.cdr.markForCheck();
    }

    viewBilling(toggleDropdown: boolean) {

        if (toggleDropdown) {
            this.toggleDropdown();
        }
        
        this.routerService.go(BILLING);
    }

    viewLocations() {
        this.toggleDropdown();
        
        this.routerService.go(LOCATIONS);
    }

    viewMyProfile() {
        this.toggleDropdown();

        this.routerService.go(PROFILE);
    }

    logout() {
        this.authService.logout();
    }

    goToDevSupport() {
        this.routerService.go(DEVSUPPORT_DASHBOARD);
    }

    goToDeliverySettings() {
        this.routerService.go(DELIVERIES_SETTINGS);
    }

    launchFeedback() {
        this.nativeWindow.open(this.feedbackUrl, '_blank');
    }
}
